import {
  AfterViewInit,
  Component,
  computed,
  effect,
  ElementRef,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
  WritableSignal,
} from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { LicenseManager } from "ag-grid-enterprise";
import {
  distinctUntilChanged,
  filter,
  from,
  map,
  Observable,
  of,
  Subject,
  switchMap,
  take,
  takeUntil,
} from "rxjs";

import { LogService } from "@bitwarden/common/abstractions/log.service";
import { GlobalService } from "@bitwarden/common/services/global/global.service";
import {
  AccountDetailsTable,
  AccountDetailsTableFilter,
} from "@bitwarden/web-vault/app/components/tables/account-tables/account-details-table/account-details.table";
import { DEFAULT_ACCOUNT_TABLE_FILTER } from "@bitwarden/web-vault/app/components/tables/account-tables/account-details-table/account-details-table.utils";
import { getAccountStatusView } from "@bitwarden/web-vault/app/components/tables/account-tables/account.table";
import { isExcludedFromAutoSync } from "@bitwarden/web-vault/app/services/syncing/sync-account-eligibility";
import { TablesFactory } from "@bitwarden/web-vault/app/components/tables/tables.factory";
import { SyncIcon } from "@bitwarden/web-vault/app/models/enum/sync.enum";
import { Origin, SyncStatusEnum } from "@bitwarden/web-vault/app/models/types/general-types";
import {
  AccountView,
  isStockOrOther,
} from "@bitwarden/web-vault/app/models/view/account/account.view";
import { TransactionView } from "@bitwarden/web-vault/app/models/view/transaction/transaction.view";

import { UserStoreService } from "@bitwarden/web-vault/app/services/store/user/user.store.service";
import { ImportStoreService } from "@bitwarden/web-vault/app/services/store/import/import.store.service";
import { SourceTransactionsView } from "@bitwarden/web-vault/app/models/view/source-transactions/source-transactions.view";
import { DialogFactory } from "@bitwarden/web-vault/app/components/dialog/dialogFactory";
import { TablesType } from "@bitwarden/web-vault/app/models/enum/tables.enum";
import { InstitutionView } from "@bitwarden/web-vault/app/models/view/institution/institution.view";
import { InstitutionStoreModel } from "@bitwarden/web-vault/app/models/store/institution.store.model";
import { CategoryView } from "@bitwarden/web-vault/app/models/view/category/category.view";
import { DEFAULT_CATEGORY_ID } from "@bitwarden/web-vault/app/models/constants/category.constants";
import { DataPluginStoreService } from "@bitwarden/web-vault/app/services/store/data-plugin/data-plugin.store.service";
import { PluginConfigService } from "@bitwarden/web-vault/app/services/store/data-plugin/plugin-config.service";
import { toObservable } from "@angular/core/rxjs-interop";
import { I18nService } from "@bitwarden/common/abstractions/i18n.service";
import {
  AccountManualType,
  AccountTableView,
  accountManualType,
} from "@bitwarden/web-vault/app/models/types/account.types";
import { GlossSync } from "@bitwarden/web-vault/app/services/syncing/gloss.sync";
import { SignalGlowService } from "@bitwarden/web-vault/app/services/signal-glow/signal-glow.service";
import { BasiqRedirectEnum } from "@bitwarden/web-vault/app/models/enum/basiq.enum";
import { FiskilRedirectEnum } from "@bitwarden/web-vault/app/services/syncing/fiskil/fiskil.enum";
import { TransactionStoreService } from "@bitwarden/web-vault/app/services/store/transaction/transaction.store.service";
import { GuideTitleEnum } from "@bitwarden/web-vault/app/models/enum/guide.enum";
import { GuideWizardService } from "@vault/web-vault/app/services/guideWizard/guide-wizard.service";
import { BasiqUserAction } from "@bitwarden/web-vault/app/services/syncing/basiq/actions/basiq.action.user";
import { FiskilApi } from "@bitwarden/web-vault/app/services/syncing/fiskil/fiskil.api";
import {
  fiskilApiIdsMissingFromVault,
  fiskilRecoveryToastKey,
  fiskilVaultAccountsMissingImportedTransactions,
  resolveFiskilBootstrapAction,
  shouldRecoverFiskilImport,
} from "@bitwarden/web-vault/app/services/syncing/fiskil/fiskil.recovery";
import { FiskilSyncStatusResponse } from "@bitwarden/web-vault/app/services/syncing/fiskil/types";
import { GlossSyncService } from "@bitwarden/web-vault/app/services/syncing/gloss.sync.service";
import { CollectionService } from "@bitwarden/common/abstractions/collection.service";
import { ToastOptions } from "@bitwarden/common/abstractions/platformUtils.service";
import { GuideSuccessToastComponent } from "@bitwarden/web-vault/app/components/guide-success-toast/guide-success-toast.component";
import { DaliService } from "@bitwarden/web-vault/app/services/dali/dali.service";
import { PartialVaultWriteError } from "@bitwarden/web-vault/app/services/dali/vault.writer";
import { ErrorResponse } from "@bitwarden/common/models/response/error.response";
import { ViewReadinessService } from "@bitwarden/web-vault/app/services/view-readiness.service";
import { CalculationTodayStoreService } from "@bitwarden/web-vault/app/services/store/calculation-today/calculation-today.store.service";
import {
  isCreditType,
  isMortgageType,
} from "@bitwarden/web-vault/app/shared/utils/helper.account/account-type";
import { AccountTypes } from "@bitwarden/web-vault/app/models/enum/account.enum";
import { isOther } from "@bitwarden/web-vault/app/models/constants/global.constants";
import { isOtherInstitutionId } from "@bitwarden/web-vault/app/shared/utils/helper.institution/default-institution-account";
import {
  clearDeepLinkQueryParam,
  deepLinkParamReady$,
} from "@bitwarden/web-vault/app/shared/utils/deep-link-selection.util";

import { formatRelativeTime } from "@bitwarden/web-vault/app/gloss/manage/manage-organizations/relative-time";

import {
  AccountListItemView,
  AccountMeterTone,
  AccountStatusLine,
  assetSharePercent,
  leveragePercent,
  shortDate,
} from "./account-list-item/account-list-item.utils";
import { countByKey } from "./manage-accounts.utils";

/* Set license */
LicenseManager.setLicenseKey(
  "Using_this_{AG_Grid}_Enterprise_key_{AG-063300}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{Ironfly_Technologies_Limited}_is_granted_a_{Single_Application}_Developer_License_for_the_application_{GLOSS_Vault}_only_for_{1}_Front-End_JavaScript_developer___All_Front-End_JavaScript_developers_working_on_{GLOSS_Vault}_need_to_be_licensed___{GLOSS_Vault}_has_been_granted_a_Deployment_License_Add-on_for_{1}_Production_Environment___This_key_works_with_{AG_Grid}_Enterprise_versions_released_before_{11_July_2025}____[v3]_[01]_MTc1MjE4ODQwMDAwMA==78426cc29fbc2c22bf97224bbd624392",
);

@Component({
  selector: "manage-account",
  templateUrl: "manage-accounts.component.html",
})
export class ManageAccountsComponent implements OnInit, OnDestroy, AfterViewInit {
  private userStore = inject(UserStoreService);
  private transactionStoreService = inject(TransactionStoreService);
  private importStore = inject(ImportStoreService);
  private dataPluginStoreService = inject(DataPluginStoreService);
  private pluginConfigService = inject(PluginConfigService);
  private glossSync = inject(GlossSync);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private globalService = inject(GlobalService);
  private logService = inject(LogService);
  protected signalGlowService = inject(SignalGlowService);
  protected guideWizardService: GuideWizardService = inject(GuideWizardService);
  private basiqUserAction: BasiqUserAction = inject(BasiqUserAction);
  private fiskilApi: FiskilApi = inject(FiskilApi);
  private glossSyncService: GlossSyncService = inject(GlossSyncService);
  private collectionService: CollectionService = inject(CollectionService);
  private daliService: DaliService = inject(DaliService);
  private viewReadiness = inject(ViewReadinessService);
  private calculationStoreService: CalculationTodayStoreService = inject(
    CalculationTodayStoreService,
  );

  private destroy$ = new Subject<void>();
  private institutionsSignal: Observable<InstitutionView[]>;
  private wasFiskilRedirect = false;
  protected readonly Origin = Origin;
  protected readonly SyncIcon = SyncIcon;

  @ViewChild("statsScroll") private statsScrollRef?: ElementRef<HTMLElement>;
  protected canScrollStatsLeft = signal(false);
  protected canScrollStatsRight = signal(false);

  // Which currency stat card (if any) is showing its exact, non-abbreviated value.
  protected expandedStat = signal<"netWorth" | "totalAssets" | "totalLiabilities" | null>(null);

  // Toggling swaps the text to a different length instantly (Angular renders it on the next
  // change-detection flush), so a plain CSS class-width transition has nothing real to animate
  // between once the new text already exceeds both guessed widths (e.g. large asset totals) —
  // the box just snaps to fit content, which read as "text pops, then the box catches up".
  // Measuring the actual before/after pixel widths (a small FLIP) and clipping with overflow
  // hidden while the box grows/shrinks between them keeps the reveal in sync for any content length.
  protected toggleExpandedStat(
    key: "netWorth" | "totalAssets" | "totalLiabilities",
    event: MouseEvent,
  ): void {
    const card = event.currentTarget as HTMLElement;
    const startWidth = card.getBoundingClientRect().width;

    this.expandedStat.update((current) => (current === key ? null : key));
    card.style.width = `${startWidth}px`;
    card.style.overflow = "hidden";

    setTimeout(() => {
      card.style.width = "auto";
      const targetWidth = card.getBoundingClientRect().width;

      // No actual width change (e.g. net worth "0" abbreviated and expanded render identically) —
      // no transition fires, so the transitionend listener below would never clean up and the card
      // would stay pinned at a fixed width with overflow hidden until the next click on it.
      if (targetWidth === startWidth) {
        card.style.width = "";
        card.style.overflow = "";
        this.updateStatsScrollArrows();
        return;
      }

      card.style.width = `${startWidth}px`;
      void card.offsetWidth; // force reflow so the width above is committed before animating
      card.style.width = `${targetWidth}px`;
      this.updateStatsScrollArrows();

      card.addEventListener(
        "transitionend",
        () => {
          card.style.width = "";
          card.style.overflow = "";
        },
        { once: true },
      );
    });
  }

  protected isFromGuide: WritableSignal<boolean> = signal(false);

  /** Single-flight guard for `shareAccountToOrgs`: true while a share is in flight, so a second
   * request (e.g. reopening the share slide-out and confirming again) can't fork the same account
   * twice — it's rejected with an info toast instead. */
  protected sharingAccountToOrg = signal(false);

  protected hasPremiumSubscription = computed(() => {
    return this.userStore.subscription.activeSubscription()?.isPremium;
  });

  protected transactionCount = computed(() => {
    const accountId = this.table.accountId();
    const transaction = this.transactionStoreService.enrichTransaction.collection();

    return transaction.filter((tx) => tx.accountLink?.id === accountId && !tx.bankImportedBalance)
      .length;
  });

  // Loading-skeleton gate. Skeleton shows while either half of a row isn't ready yet:
  //   1. table.data() nullish (null initial sentinel / undefined) — the enriched accounts
  //      collection hasn't resolved; an empty array means loaded-but-empty (so no skeleton).
  //   2. !todayViewReady() — today balance calculations ("balance-calculation-today") haven't
  //      completed, so per-account balances aren't available yet and would render blank. This is
  //      the same latched readiness signal the Today accounts widget keys its skeleton off
  //      (views.component.ts showTodaySkeleton). It latches true after the first calc, so later
  //      recalcs (filter change / sync) don't re-flash the skeleton.
  protected showAccountsSkeleton = computed(
    () => this.table.data() == null || !this.viewReadiness.todayViewReady(),
  );

  private summaryAccounts = computed(() => this.userStore.enrichAccounts.collection() ?? []);

  // AccountView.c_balance isn't reliably populated on the raw enrichAccounts collection — the
  // real per-account balance only exists in calculationStoreService.balanceByAccount.balances(),
  // the same Map getAccountTableViews() uses to fill in AccountTableView.balance for the list rows.
  private summaryBalances = computed(() =>
    this.calculationStoreService.balanceByAccount.balances(),
  );

  private accountBalance(account: AccountView): number {
    return this.summaryBalances()?.get(account.id) ?? 0;
  }

  // ponytail: liability accounts are stored negative by every aggregator sync path (see
  // isCreditType/isMortgageType usage in plaid.sync.ts, basiq.action.account.ts, fiskil.sync.ts)
  // — but NOT for manually-created credit/loan accounts, which just store whatever positive
  // number the user typed. Partition by type, not balance sign, and normalize the sign
  // ourselves (see displayBalance()) rather than trusting the underlying storage convention.
  protected isLiabilityAccount(type: string): boolean {
    return isCreditType(type) || isMortgageType(type);
  }

  // Always shows liabilities as a negative magnitude, regardless of whether the underlying
  // balance happens to be stored negative (aggregator-synced) or positive (manual entry) — a
  // manual credit account otherwise displayed as a plain positive number, colored green like
  // an asset, which reads as "I have $1k" instead of "I owe $1k".
  protected displayBalance(account: { balance: number; type: string }): number {
    return this.isLiabilityAccount(account.type)
      ? -Math.abs(account.balance ?? 0)
      : (account.balance ?? 0);
  }

  protected totalAssets = computed(() =>
    this.summaryAccounts()
      .filter((account) => !this.isLiabilityAccount(account.type))
      .reduce((sum, account) => sum + this.accountBalance(account), 0),
  );

  // Math.abs(), not a leading negation: liability balances are stored negative by every
  // aggregator sync path (see isCreditType/isMortgageType usage in plaid.sync.ts etc.), but a
  // manually-created credit account's balance is just whatever positive number the user typed —
  // negating that would flip totalLiabilities (and leverageRate) negative instead of reflecting
  // debt magnitude regardless of how the underlying balance happens to be signed.
  protected totalLiabilities = computed(() =>
    this.summaryAccounts()
      .filter((account) => this.isLiabilityAccount(account.type))
      .reduce((sum, account) => sum + Math.abs(this.accountBalance(account)), 0),
  );

  protected netWorth = computed(() => this.totalAssets() - this.totalLiabilities());

  protected accountCount = computed(() => this.summaryAccounts().length);

  protected leverageRate = computed(() => {
    const assets = this.totalAssets();
    if (assets > 0) {
      return Math.round((this.totalLiabilities() / assets) * 100);
    }
    // Zero assets with outstanding liabilities (e.g. the only tracked account is a mortgage) is
    // maximally leveraged, not 0% — the naive assets>0 ? … : 0 fallback would show "0% Leveraged"
    // for the most-leveraged case there is.
    return this.totalLiabilities() > 0 ? 100 : 0;
  });

  // ── The list ───────────────────────────────────────────────────────────────────────────────

  protected cards = computed<AccountListItemView[]>(() =>
    (this.table.data() ?? []).map((row) => this.toCardView(row)),
  );

  private toCardView(row: AccountTableView): AccountListItemView {
    const isLiability = this.isLiabilityAccount(row.type);
    const balance = this.displayBalance(row);
    const assets = this.totalAssets();
    const share = isLiability
      ? leveragePercent(row.balance ?? 0, assets)
      : assetSharePercent(row.balance ?? 0, assets);

    return {
      id: row.id,
      row,
      name: this.getAccountName(row),
      subtitle: this.getAccountSubtitle(row),
      tag: this.getOriginLabel(row),
      // A liability reads as what is owed ("1,000.00 AUD owed"), however the balance is stored.
      balance: isLiability ? Math.abs(balance) : balance,
      amountTone: isLiability || balance < 0 ? "negative" : balance > 0 ? "positive" : "zero",
      unit: isLiability ? this.i18nService.t("amountOwed", row.symbol) : row.symbol,
      isLiability,
      meter: {
        // The bar can't run past full even when the debt is bigger than the assets.
        percent: Math.min(100, share),
        tone: this.getMeterTone(row.type),
        label: this.i18nService.t(isLiability ? "leverageOnAssets" : "shareOfAssets", share),
      },
      status: this.getStatusLine(row),
    };
  }

  private getOriginLabel(row: AccountTableView): string {
    switch (row.original.origin) {
      case Origin.manual:
        return this.i18nService.t("manualAccountBadge");
      case Origin.fiskil:
        return "Fiskil";
      case Origin.basiq:
        return "Basiq";
      case Origin.plaid:
        return "Plaid";
      case Origin.plugin:
        return this.i18nService.t("accountOriginPlugin");
      default:
        return this.i18nService.t("accountOriginSystem");
    }
  }

  // Credit-card style debt reads amber and loan / mortgage debt red, as the old bucket meter did.
  private getMeterTone(type: string): AccountMeterTone {
    if (type === AccountTypes.mortgage || type === AccountTypes.loan) {
      return "debt";
    }
    if (type === AccountTypes.creditCard || type === AccountTypes.credit) {
      return "credit";
    }
    return "asset";
  }

  private getStatusLine(row: AccountTableView): AccountStatusLine {
    // Manual, system (Gloss Credits) and org-owned accounts have no fetch to report, so they read
    // as last touched by the user. A plugin account is also kept out of "Fetch all", but its own
    // fetches leave sync records, so it reports those like any synced account.
    if (isExcludedFromAutoSync(row.original) && row.original.origin !== Origin.plugin) {
      const touched = row.original.dateModified ?? row.original.dateCreated;
      return {
        level: "neutral",
        text: touched ? this.i18nService.t("updatedOnDate", shortDate(touched)) : "",
        syncing: false,
      };
    }

    // With no sync records at all the table invents a "synced just now" for every account; that
    // was never a fetch, so there is nothing to say.
    const status = (this.importStore.syncStatus.syncStatusViews() ?? []).length
      ? row.statusPoint
      : undefined;
    if (!status) {
      return { level: "neutral", text: "", syncing: false };
    }
    if (status.key === SyncStatusEnum.synced) {
      return {
        level: "ok",
        text: this.i18nService.t(
          "fetchedTimeAgo",
          formatRelativeTime(status.dateCreated, this.i18nService),
        ),
        syncing: false,
      };
    }
    if (status.key === SyncStatusEnum.syncing) {
      return {
        level: "warn",
        text: this.getStatusText(status.messageKey),
        syncing: true,
        icon: status.icon,
        spin: status.icon === SyncIcon.syncAll,
      };
    }
    const when = formatRelativeTime(status.dateCreated, this.i18nService);
    // A plain failure reads "Fetch failed"; the other states (no consent, no connector, …) keep
    // their own wording.
    const what =
      status.key === SyncStatusEnum.failed
        ? this.i18nService.t("fetchFailed")
        : this.getStatusText(status.messageKey);
    return { level: "err", text: [what, when].filter(Boolean).join(" · "), syncing: false };
  }

  // ponytail: "needs attention" = latest sync status is neither synced nor syncing — the same
  // definition account-details.component.ts's SYNC_TAG already uses (`default: SYNC_TAG.error`).
  //
  // Reads summaryAccounts() (Kevin review #3765), the same unfiltered collection netWorth/
  // totalAssets/totalLiabilities/accountCount already use — table.data() is filtered by the
  // accounts filter (e.g. "manual only"), so reading it here let an applied filter silently drop
  // a needs-attention account from this count while the rest of the band still reflected the
  // whole portfolio. getAccountStatusView() is the same per-account status lookup table.data()
  // itself uses (see account.table.ts's getAccountTableViews()).
  protected accountsNeedingAttention = computed(() => {
    const syncStatusViews = this.importStore.syncStatus.syncStatusViews() ?? [];
    return this.summaryAccounts().filter((account) => {
      const key = getAccountStatusView(account, syncStatusViews)?.key;
      return key && key !== SyncStatusEnum.synced && key !== SyncStatusEnum.syncing;
    }).length;
  });

  localEnv = false;
  loading = false;
  isInfo = true;
  accountViews!: AccountView[];
  sourceTransactions!: SourceTransactionsView[];
  progressBarPercent = 0;
  i18nService = inject(I18nService);
  dialogFactory: DialogFactory;
  table: AccountDetailsTable = new TablesFactory().get(TablesType.AccountDetails, this);

  // Route params are available immediately, but on a cold load (bookmark, refresh, pasted link)
  // the accounts store hasn't populated yet. combineLatest with the table's data (rather than a
  // one-shot queryParams read) covers both timings (#4519 follow-up).
  private readonly accountId$ = this.route.queryParams.pipe(
    map((params) => params["accountId"] as string | undefined),
    distinctUntilChanged(),
  );

  // Filter options
  manualAccountTypes: AccountManualType[] = Object.keys(accountManualType) as AccountManualType[];
  // Basiq/Plaid accounts still sync normally — they're just no longer their own filter-dropdown
  // entry (AccountsFilterComponent.displayedSyncedOrigins hides them, leaving them permanently
  // shown/unfilterable) now that Fiskil supersedes them. Counting them below would just be wasted
  // work on every accounts change with nothing reading the result (#4514 follow-up).
  syncedOrigins: Origin[] = [Origin.fiskil];

  // Counts shown next to each filter option — zero-count types/origins are disabled in the
  // filter dropdown, so these must count against the full (unfiltered) account collection.
  // While enrichAccounts hasn't resolved yet (null), leave counts empty rather than defaulting
  // every type to 0 — AccountsFilterComponent treats a missing count as "not yet known" (not
  // disabled) so the dropdown doesn't grey out every option during the loading window.
  // Both counts share countByKey (manage-accounts.utils.ts) so that null-guard/semantics can't
  // drift between them (#4514 follow-up).
  protected manualTypeCounts = computed(() =>
    countByKey(this.userStore.enrichAccounts.collection(), this.manualAccountTypes, (account) =>
      account.origin === Origin.manual ? (account.type as AccountManualType) : undefined,
    ),
  );

  protected syncedOriginCounts = computed(() =>
    countByKey(
      this.userStore.enrichAccounts.collection(),
      this.syncedOrigins,
      (account) => account.origin,
    ),
  );

  // Deep-cloned so per-instance filter mutations never leak into the shared
  // default constant.
  selectedFilter: AccountDetailsTableFilter = structuredClone(DEFAULT_ACCOUNT_TABLE_FILTER);

  constructor() {
    this.dialogFactory = new DialogFactory();

    this.institutionsSignal = toObservable(
      this.dataPluginStoreService.theInstitutionList.theInstitutionViews,
    );

    effect(() => {
      this.accountViews = this.userStore.enrichAccounts.collection();
      this.sourceTransactions = this.importStore.sourceTransaction.sourceTransactionViews();
    });

    // The stat cards' width depends on netWorth()/totalAssets()/etc., which are still near-empty
    // at ngAfterViewInit if account data hasn't loaded yet — nothing previously rechecked the
    // scroll arrows once the real (wider) values arrived, so overflow could go undetected.
    effect(() => {
      this.netWorth();
      this.totalAssets();
      this.totalLiabilities();
      this.accountCount();
      setTimeout(() => this.updateStatsScrollArrows());
    });

    deepLinkParamReady$(this.accountId$, toObservable(this.table.data))
      .pipe(takeUntil(this.destroy$))
      .subscribe((accountId) => {
        // Checked against the unfiltered account collection, not this.table.data() — that's
        // post-filterAccountRows, so an account the user has since filtered out of view would
        // otherwise look "stale" here and get swept from the URL even though it still exists.
        const matchingAccount = this.userStore.enrichAccounts
          .collection()
          .find((acc) => acc.id === accountId);
        if (matchingAccount) {
          if (this.table.accountId() !== matchingAccount.id) {
            this.table.onRowClicked(matchingAccount.id);
          }
        } else {
          // No accompanying clear on a match: the search bar is the only live producer of this
          // param and it already resolved the account against the store, so a matched accountId
          // should stay in the URL (copy/shareable), matching pre-#4519 observable behaviour.
          // Only a genuinely stale id (the data is confirmed loaded here — deepLinkParamReady$
          // only emits once loaded$ is an array) gets swept from the URL.
          this.clearAccountIdFromUrl();
        }
      });
  }

  ngAfterViewInit(): void {
    this.updateStatsScrollArrows();
  }

  @HostListener("window:resize")
  onStatsScrollResize(): void {
    this.updateStatsScrollArrows();
  }

  protected updateStatsScrollArrows(): void {
    const el = this.statsScrollRef?.nativeElement;
    if (!el) {
      return;
    }
    this.canScrollStatsLeft.set(el.scrollLeft > 0);
    this.canScrollStatsRight.set(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }

  protected scrollStats(direction: 1 | -1): void {
    this.statsScrollRef?.nativeElement.scrollBy({ left: direction * 160, behavior: "smooth" });
  }

  async ngOnInit() {
    if (process.env.NODE_ENV === "development") {
      this.localEnv = true;
    }

    this.route.queryParams
      .pipe(
        switchMap((params) => {
          // Selection itself now lives in the deepLinkParamReady$ subscription above; this only
          // restores accountId's precedence over the branches below, so a URL carrying both
          // accountId and (e.g.) a Basiq jobIds param doesn't have the redirect handling steal it.
          if (params["accountId"]) {
            return of(true);
          } else if (params[BasiqRedirectEnum.jobIds]) {
            return from(this.handleBasiqRedirect(params));
          } else if (params["isFromGuide"] === "true" && params["guide"]) {
            this.isFromGuide.set(true);
            if (params["guide"] === GuideTitleEnum.addManualAccount) {
              this.dialogFactory.selectInstitutionsDialog("bank", this.isFromGuide());
            } else if (params["guide"] === GuideTitleEnum.fetchAnAccount) {
              this.dialogFactory.createLinkToBasiqAccount(
                this.isFromGuide(),
                accountManualType.bank,
              );
            }
          } else if (
            params[FiskilRedirectEnum.status] &&
            params[FiskilRedirectEnum.redirect] &&
            params[FiskilRedirectEnum.status] === FiskilRedirectEnum.success &&
            params[FiskilRedirectEnum.redirect] === FiskilRedirectEnum.fiskil
          ) {
            return from(this.handleFiskilRedirect(params));
          }
          return of(false);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((res) => this.logService.info(`Basiq redirect handled with result: ${res}`));

    const qp = this.route.snapshot.queryParams;
    const isFiskilRedirectNow =
      qp[FiskilRedirectEnum.status] === FiskilRedirectEnum.success &&
      qp[FiskilRedirectEnum.redirect] === FiskilRedirectEnum.fiskil;
    if (!isFiskilRedirectNow) {
      void this.recoverFiskilImportIfNeeded();
    }
    // Plaid imports run inline from Link onSuccess (no redirect to this page), so always check.
    void this.glossSync.recoverPlaidSessionIfNeeded();
  }

  /** Drop a stale deep-link query param once we're sure the account it names doesn't exist. */
  private clearAccountIdFromUrl(): void {
    clearDeepLinkQueryParam(this.router, this.route, "accountId");
  }

  onFilterChange(updatedFilter: AccountDetailsTableFilter) {
    this.selectedFilter = updatedFilter;

    this.table.setFilter(this.selectedFilter);
  }

  private async handleBasiqRedirect(queryParamMap: Record<string, string>) {
    const jobIds = queryParamMap[BasiqRedirectEnum.jobIds];
    const state = queryParamMap[BasiqRedirectEnum.state];

    const shouldCallBasiq = this.isRedirectFromBasiq(jobIds);
    if (shouldCallBasiq && state === BasiqRedirectEnum.resync) {
      return await this.resyncAllAfterInstosInitialized();
    }
    if (shouldCallBasiq) {
      const response = await this.importAfterInstosInitialized(jobIds);
      if (await this.basiqUserAction.getActiveConsent()) {
        await this.guideWizardService.completeTask(
          this.guideWizardService.getTaskByGuideTitle(GuideTitleEnum.fetchAnAccount),
        );
        this.dialogFactory.openGuideDialog();
      }
      return response;
    }
    return false;
  }

  private async handleFiskilRedirect(queryParamMap: Record<string, string>) {
    const status = queryParamMap[FiskilRedirectEnum.status];
    const redirect = queryParamMap[FiskilRedirectEnum.redirect];
    const isFromGuide = queryParamMap["isFromGuide"] === "true";

    const isRedirectFromFiskil =
      status === FiskilRedirectEnum.success && redirect === FiskilRedirectEnum.fiskil;

    if (isRedirectFromFiskil) {
      this.wasFiskilRedirect = true;
      this.clearUrlFromFiskilParams();
      void this.pluginConfigService.ensureFiskilPluginActivated();
      this.importFiskilAfterInstosInitialized();
      if (isFromGuide) {
        await this.guideWizardService.completeTask(
          this.guideWizardService.getTaskByGuideTitle(GuideTitleEnum.fetchAnAccount),
        );
      }
      return true;
    }
    return false;
  }

  async startSync() {
    if (!this.hasPremiumSubscription()) {
      return;
    }

    await this.glossSync.syncAll(true);
  }

  private isRedirectFromBasiq(jobIds: string) {
    if (jobIds === "null" || !jobIds || jobIds === "undefined") {
      return false;
    }
    this.clearUrlFromBasiqParams();

    return true;
  }

  private clearUrlFromBasiqParams() {
    const currentURL = new URL(window.location.href);
    currentURL.searchParams.delete("jobIds");
    const hashIndex = currentURL.href.indexOf("#");
    const updatedURL = `${currentURL.href.substring(0, hashIndex)}#/manage/account`;
    history.replaceState({}, document.title, updatedURL);
  }

  private clearUrlFromFiskilParams() {
    const currentURL = new URL(window.location.href);
    currentURL.searchParams.delete(FiskilRedirectEnum.status);
    currentURL.searchParams.delete(FiskilRedirectEnum.redirect);
    const hashIndex = currentURL.href.indexOf("#");
    const updatedURL = `${currentURL.href.substring(0, hashIndex)}#/manage/account`;
    history.replaceState({}, document.title, updatedURL);
  }

  showGlobalError() {
    this.globalService.showErrorMessage("error", "somethingWentWrong");
  }

  protected getStatusText(messageKey?: string) {
    if (!messageKey) {
      return "";
    }

    const translatedText = this.i18nService.t(messageKey);
    const displayText = translatedText === messageKey ? messageKey : translatedText;
    const normalizedText = displayText.trim().toLowerCase();

    if (normalizedText.includes("error")) {
      return this.i18nService.t("error");
    }

    return displayText;
  }

  protected showInterest(account: AccountTableView): boolean {
    return (
      !isStockOrOther(account.type) &&
      !this.isOnOtherInstitution(account) &&
      !isOther(account.original?.c_institutionAccountType?.name) &&
      !Number.isNaN(account.interestRate)
    );
  }

  protected getInterestRate(account: AccountTableView): string {
    return `${account.interestRate}% p.a.`;
  }

  protected getAccountSubtitle(account: AccountTableView): string {
    const accountType = this.getDeduplicatedName(account);
    const rateText = this.showInterest(account) ? this.getInterestRate(account) : "";

    return [accountType, rateText].filter(Boolean).join(" · ");
  }

  // Keyed on the placeholder institution's id, not the account type or its "Other" name: synced
  // investment accounts land on it too, and a user's own institution named "Other" isn't it.
  private isOnOtherInstitution(account: AccountTableView): boolean {
    return isOtherInstitutionId(account.original?.institutionLink?.institutionId);
  }

  protected getInstitution(account: AccountTableView): string {
    return this.isOnOtherInstitution(account) ? "" : account.institution?.trim();
  }

  protected getAccountName(account: AccountTableView): string {
    return [this.getInstitution(account), account.name?.trim()].filter(Boolean).join(" ");
  }

  private getSourceName(account: AccountTableView): string {
    // The placeholder institution only carries the placeholder "Other" product.
    if (this.isOnOtherInstitution(account)) {
      return "";
    }

    const productName = account.original.c_institutionAccountType?.name?.trim();

    // A stock/other account at a real institution still defaults to that institution's "Other" product.
    return isStockOrOther(account.type) && isOther(productName)
      ? ""
      : productName || account.name?.trim();
  }

  private getDeduplicatedName(account: AccountTableView): string {
    const institutionName = this.getInstitution(account);
    const sourceAccountName = this.getSourceName(account);

    if (!sourceAccountName) {
      return "";
    }

    if (!institutionName) {
      return sourceAccountName;
    }

    return this.removeSameWords(institutionName, sourceAccountName);
  }

  private removeSameWords(leftValue: string, rightValue: string): string {
    const leftWords = leftValue.split(/\s+/).filter(Boolean);
    const rightWords = rightValue.split(/\s+/).filter(Boolean);
    let sharedWordCount = 0;

    while (
      sharedWordCount < leftWords.length &&
      sharedWordCount < rightWords.length &&
      leftWords[sharedWordCount].toLowerCase() === rightWords[sharedWordCount].toLowerCase()
    ) {
      sharedWordCount += 1;
    }

    return rightWords.slice(sharedWordCount).join(" ") || rightValue;
  }

  /**
   * take(1) : Coming from basiq it might not have been initialized before the import call, so this
   * is to make sure they are initialized and there will bo no error down the road
   * */
  private async importAfterInstosInitialized(jobIds: string) {
    return await this.glossSync.import(Origin.basiq, jobIds);
  }

  /**
   * Coming from Fiskil consent redirect: fetch accounts and transactions.
   */
  private importFiskilAfterInstosInitialized() {
    this.institutionsSignal
      .pipe(
        filter((ins) => ins !== null),
        take(1),
      )
      // eslint-disable-next-line rxjs-angular/prefer-takeuntil
      .subscribe(() => {
        this.glossSync.import(Origin.fiskil, "");
      });
  }

  /**
   * Session recovery: if backend reports unsaved/partial Fiskil data, re-run import in recovery mode.
   */
  private async recoverFiskilImportIfNeeded(): Promise<void> {
    if (this.wasFiskilRedirect) {
      return;
    }
    if (!this.hasPremiumSubscription()) {
      return;
    }
    if (!this.pluginConfigService.hasEnabledFiskilPluginConfig()) {
      return;
    }
    if (this.glossSyncService.runningSyncState().isRunning) {
      return;
    }

    try {
      const { consents = [] } = await this.fiskilApi.getActiveConsents();
      if (consents.length === 0) {
        return;
      }

      const status: FiskilSyncStatusResponse = await this.fiskilApi.getSyncStatus();
      const vaultFiskilAccounts = this.userStore.getFiskilAccounts();

      let apiAccountIds: string[] | null = null;
      if (!shouldRecoverFiskilImport(status)) {
        apiAccountIds = await this.fiskilApi.listApiAccountIdsWithRetry();
        if (apiAccountIds === null) {
          this.logService.warning(
            "Fiskil recovery: could not list API accounts for vault gap check after retry.",
          );
        }
      }

      const vaultAccountsMissingTransactions = fiskilVaultAccountsMissingImportedTransactions(
        vaultFiskilAccounts,
        this.transactionStoreService.transaction.transactionViews(),
      );

      const action = resolveFiskilBootstrapAction(status, apiAccountIds, vaultFiskilAccounts, {
        vaultAccountsMissingTransactions,
      });
      if (action !== "recovery") {
        return;
      }

      const toastKey =
        (status.missing_accounts?.length ?? 0) > 0 ||
        vaultAccountsMissingTransactions ||
        (apiAccountIds != null &&
          fiskilApiIdsMissingFromVault(apiAccountIds, vaultFiskilAccounts).length > 0)
          ? "fiskilRecoverMissingAccounts"
          : fiskilRecoveryToastKey(status);

      this.logService.info("Recovering Fiskil import (sync-status or missing vault accounts).");
      this.globalService.showInfoMessage("info", toastKey);
      await this.glossSync.import(Origin.fiskil, { mode: "recovery", syncStatus: status });
    } catch (error) {
      this.logService.warning(
        `Fiskil recovery check skipped due to sync-status read error: ${JSON.stringify(error)}`,
      );
    }
  }

  /**
   * take(1) : Coming from basiq it might not have been initialized before the syncAll call, so this
   * is to make sure they are initialized and there will bo no error down the road
   * */
  private async resyncAllAfterInstosInitialized() {
    return await this.glossSync.syncAll();
  }

  closeAccountDetails() {
    this.table.accountId.set(null);
  }

  openAccountCreateModal() {
    this.dialogFactory.selectAccountManualTypeDialog();
  }

  /**
   * Single-collection convenience over `shareAccountToOrgs`.
   */
  async shareAccountToOrg(collectionId: string, accountId: string) {
    await this.shareAccountToOrgs([collectionId], accountId);
  }

  /**
   * Shares the account into each selected team (collection). The collections are grouped by
   * organization so the account is forked into each org at most once (all of that org's selected
   * collections go on the single fork) — forking per collection would leave duplicate ciphers in
   * the same org, since forking never marks the personal original as forked. If the account is
   * already forked into an org, the existing fork is just shared into the additional collections.
   */
  async shareAccountToOrgs(collectionIds: string[], accountId: string) {
    // Guarded synchronously (before any `await`) so a second request dispatched while this one is
    // still in flight can't slip through and fork the account twice. Tell the user instead of
    // silently dropping the second request.
    if (this.sharingAccountToOrg()) {
      this.globalService.showInfoMessage("", "shareInProgress");
      return;
    }
    this.sharingAccountToOrg.set(true);

    try {
      const account = this.userStore.accounts.accountViews().find((a) => a.id === accountId);

      if (!account) {
        // Picker fired with a stale accountId (e.g. row selection changed mid-flight). Without
        // this, the picker just clears its selection and the spinner flashes with nothing to
        // show the user — indistinguishable from a share that silently failed.
        this.logService.warning(
          `shareAccountToOrg: no account found for id "${accountId}"; skipping share.`,
        );
        this.globalService.showErrorMessage("error", "accountNoLongerAvailable");
        return;
      }

      const collectionsByOrg = new Map<string, { id: string; name: string }[]>();
      for (const collectionId of new Set(collectionIds)) {
        const org = await this.getOrganizationForCollectionOrWarn(collectionId);
        if (org) {
          collectionsByOrg.set(org.organizationId, [
            ...(collectionsByOrg.get(org.organizationId) ?? []),
            { id: collectionId, name: org.collectionName },
          ]);
        }
      }

      const sharedCollectionNames: string[] = [];
      for (const [orgId, orgCollections] of collectionsByOrg) {
        try {
          sharedCollectionNames.push(
            ...(await this.shareAccountToOrgCollections(account, orgId, orgCollections)),
          );
        } catch (e) {
          // One org failing shouldn't stop the remaining orgs from being shared.
          this.globalService.showMessage("error", "Error", this.getErrorMessage(e), false);
        }
      }
      // Always notify, even when no names were collected: a partial failure (e.g. col-1 shared,
      // then col-2 throws) loses the names but the account did land in col-1, so listeners must
      // reload. An extra reload is harmless. Tells AccountLinkedCollectionComponent (and anything
      // else showing "who's this shared with") to reload — see DaliService.sharedVersion for why
      // that can't just watch the account object itself.
      this.daliService.notifyShared();
      // One confirmation for the whole action (not one per org): several guide toasts would
      // stack up and push any warnings off-screen on mobile. Collection ids are already
      // deduped above, so no need to dedupe names.
      if (sharedCollectionNames.length) {
        this.showSharedToCollectionToast(sharedCollectionNames);
      }
    } catch (e) {
      this.globalService.showMessage("error", "Error", this.getErrorMessage(e), false);
    } finally {
      this.sharingAccountToOrg.set(false);
    }
  }

  /**
   * Forks `account` into `orgId` with all of `collectionIds` — unless it's already been forked
   * there, in which case the existing forked cipher is just shared into each additional
   * collection, instead of creating a duplicate cipher.
   */
  private async shareAccountToOrgCollections(
    account: AccountView,
    orgId: string,
    collections: { id: string; name: string }[],
  ): Promise<string[]> {
    const collectionIds = collections.map((c) => c.id);
    if (account.isOrganizationOwned(orgId)) {
      // Only collections the account actually landed in — it may already have been in some of
      // the others, and the confirmation shouldn't claim a share that didn't happen.
      const sharedNames: string[] = [];
      let skippedCategoryNames: string[] = [];
      let foreignOrgCategoryNames: string[] = [];
      for (const { id: collectionId, name } of collections) {
        const result = await this.addCollectionToExistingFork(account, orgId, collectionId);
        if (result.accountShared) {
          sharedNames.push(name);
        }
        // Same transactions/categories for every collection of this fork, so the skipped lists
        // are identical per call — keep one copy rather than repeating the toast per collection.
        skippedCategoryNames = result.skippedCategoryNames;
        foreignOrgCategoryNames = result.foreignOrgCategoryNames;
      }
      if (!sharedNames.length) {
        // The account cipher was already in these collections — addCollectionToExistingFork was
        // a no-op for it. Say so instead of claiming a share that didn't happen.
        this.globalService.showInfoMessage("", "alreadyInCollection");
      }
      if (skippedCategoryNames.length) {
        // Real signal instead of a console-only warning: the user needs to know some of this
        // account's transactions still point at a personal category other org members can't see.
        this.globalService.showMessage(
          "warning",
          "",
          this.i18nService.t(
            "categoriesNotSharedStillPersonal",
            String(skippedCategoryNames.length),
          ),
          false,
        );
      }
      if (foreignOrgCategoryNames.length) {
        // Distinct cause from the "still personal" toast above: these categories aren't
        // personal at all, they belong to a *different* org the current org can't see into.
        this.globalService.showMessage(
          "warning",
          "",
          this.i18nService.t(
            "categoriesNotSharedForeignOrg",
            String(foreignOrgCategoryNames.length),
          ),
          false,
        );
      }
      return sharedNames;
    }

    const acc = account.toStoreModel();
    acc.vid = "";
    // A cross-org share (orgA account into an orgB collection) mustn't leave the fork claiming
    // orgA. VaultWriter restamps oid on save, but the in-memory fork view would still say orgA.
    acc.oid = undefined;
    acc.id = crypto.randomUUID();
    acc.name = `Shared ${acc.name}`;

    const { skippedCategoryIds, foreignOrgCategoryIds } = await this.forkAccountForOrg(
      new AccountView(acc),
      orgId,
      collectionIds,
      account,
    );
    if (skippedCategoryIds.length) {
      // Distinct from the addCollectionToExistingFork path's "still personal" toast: these ids
      // failed to resolve in categoryViews() at all (deleted, or a stale local snapshot) rather
      // than being a category that's genuinely still personal — "re-categorize" is the right
      // instruction either way, but claiming it's "still personal" would be factually wrong here.
      this.globalService.showMessage(
        "warning",
        "",
        this.i18nService.t("categoriesNotSharedMissing", String(skippedCategoryIds.length)),
        false,
      );
    }
    if (foreignOrgCategoryIds.length) {
      // Same cause as addCollectionToExistingFork's foreign-org toast above: these categories
      // belong to a different org this org can't see into, so forkCategoriesForOrg left them
      // unforked rather than cloning another org's data into this one.
      this.globalService.showMessage(
        "warning",
        "",
        this.i18nService.t("categoriesNotSharedForeignOrg", String(foreignOrgCategoryIds.length)),
        false,
      );
    }
    return collections.map((c) => c.name);
  }

  // Adds the collection to the already-forked org account, institution and transactions rather
  // than re-forking them, so sharing the same account into a second collection doesn't create
  // duplicate ciphers — and its institution/transactions stay visible alongside it.
  // TODO: once StoreModelCollection grows a `share()` (see model.collection.abstraction.ts), route
  // these through `userStore.accounts.share(...)` etc. instead of calling daliService.share directly.
  //
  // Returns whether the account cipher itself was actually shared — `false` means the account was
  // already in `collectionId` (daliService.share is idempotent), so the caller can tell that
  // no-op apart from a real share instead of reporting success for something that didn't happen —
  // plus the names of any linked categories that couldn't be shared (see below), so the caller can
  // surface that to the user instead of it only landing in a console warning.
  private async addCollectionToExistingFork(
    existingFork: AccountView,
    orgId: string,
    collectionId: string,
  ): Promise<{
    accountShared: boolean;
    skippedCategoryNames: string[];
    foreignOrgCategoryNames: string[];
  }> {
    const vaultId = existingFork.toStoreModel().vid;
    if (!vaultId) {
      throw new Error("Forked account is missing a vault id");
    }
    const accountShared = await this.daliService.share(vaultId, orgId, collectionId);

    const institutionVid = this.findInstitutionForAccount(existingFork)?.toStoreModel().vid;
    if (institutionVid) {
      await this.daliService.share(institutionVid, orgId, collectionId);
    }

    const linkedTransactions = this.transactionsLinkedToAccount(existingFork.id);

    const forkedTransactionVids = new Set(
      linkedTransactions.map((tx) => tx.toStoreModel().vid).filter((vid): vid is string => !!vid),
    );
    await this.shareVids(forkedTransactionVids, orgId, collectionId);

    // Categories referenced by these transactions are usually already org-owned (forked
    // alongside the transactions when the account was first forked, see forkCategoriesForOrg),
    // but a transaction can pick up a still-personal category afterward — the category picker
    // draws from a merged personal+org list with no ownership filter. This function only adds a
    // collection to an already-forked account, it doesn't re-fork anything (see doc comment
    // above), so don't fork a stray personal category here either — daliService.share moves the
    // cipher it's given into the org, and calling it on a category that's still personal would
    // rip that category out of the user's personal vault instead of cloning it, breaking any
    // other personal transaction still pointing at it. Forking it here would also decide, on the
    // user's behalf, that it should now be visible to `collectionId` — an unrequested over-share.
    // So: only share categories already org-owned by *this* org, and report (don't just log) any
    // that aren't, so the user can go re-categorize the affected transaction(s) instead of the gap
    // sitting silent. `isOrganizationOwned(orgId)` false doesn't mean "personal" though — a category
    // can also be owned by a *different* org (e.g. picked via the same ownership-unfiltered picker).
    // Distinguish the two with the no-arg `isOrganizationOwned()` check, so a foreign-org category
    // isn't misreported to the user as "still personal".
    const linkedCategories = this.categoriesLinkedToTransactions(linkedTransactions);
    const forkedCategoryVids = new Set<string>();
    const skippedCategoryNames: string[] = [];
    const foreignOrgCategoryNames: string[] = [];
    for (const cat of linkedCategories) {
      if (!cat.isOrganizationOwned(orgId)) {
        if (cat.isOrganizationOwned()) {
          this.logService.warning(
            `addCollectionToExistingFork: category "${cat.id}" is owned by a different organization; skipping share.`,
          );
          foreignOrgCategoryNames.push(cat.name);
        } else {
          this.logService.warning(
            `addCollectionToExistingFork: category "${cat.id}" is not org-owned; skipping share.`,
          );
          skippedCategoryNames.push(cat.name);
        }
        continue;
      }
      const vid = cat.toStoreModel().vid;
      if (vid) {
        forkedCategoryVids.add(vid);
      }
    }
    await this.shareVids(forkedCategoryVids, orgId, collectionId);

    return { accountShared, skippedCategoryNames, foreignOrgCategoryNames };
  }

  // Shares each vid into `collectionId` one at a time — daliService doesn't expose a batch share,
  // so this is still a share per cipher, just de-duplicated across the two loops that used to
  // repeat this same pattern independently (transactions and categories, above).
  private async shareVids(
    vids: Iterable<string>,
    orgId: string,
    collectionId: string,
  ): Promise<void> {
    for (const vid of vids) {
      await this.daliService.share(vid, orgId, collectionId);
    }
  }

  private findInstitutionForAccount(account: AccountView): InstitutionView | undefined {
    return this.userStore.institutions
      .institutionViews()
      .find((i) => i.id === account.institutionLink?.institutionId);
  }

  private transactionsLinkedToAccount(accountId: string): TransactionView[] {
    return this.transactionStoreService.transaction
      .transactionViews()
      .filter((tx) => tx.accountLink?.id === accountId);
  }

  // The default category (fixed id, present in every vault) is excluded: it doesn't need
  // forking/sharing, so remapping/dedup logic below only ever deals with user-created categories.
  private uniqueCategoryIds(transactions: TransactionView[]): string[] {
    const ids = new Set<string>();
    for (const tx of transactions) {
      for (const allocation of tx.allocations) {
        if (allocation.categoryLink?.id && allocation.categoryLink.id !== DEFAULT_CATEGORY_ID) {
          ids.add(allocation.categoryLink.id);
        }
      }
    }
    return Array.from(ids);
  }

  private categoriesLinkedToTransactions(transactions: TransactionView[]): CategoryView[] {
    const categoryViews = this.userStore.categories.categoryViews() ?? [];
    return this.uniqueCategoryIds(transactions)
      .map((id) => categoryViews.find((cat) => cat.id === id))
      .filter((cat): cat is CategoryView => !!cat);
  }

  // Forks any non-default category used by `transactions` that isn't already owned by
  // `organization`, so the shared transactions' categories resolve for other org members instead
  // of pointing back at a personal-vault category cipher they can't see. Returns the id -> id fork
  // map for the caller to remap `categoryLink.id` with (categories already org-owned are left out
  // of the map since no remap is needed for them), plus the ids of any categories referenced by
  // `transactions` that couldn't be resolved at all (deleted, or a stale local `categoryViews()`
  // snapshot). Distinct cause from the "still personal" skip in addCollectionToExistingFork below,
  // so the caller surfaces it under its own message (categoriesNotSharedMissing) instead of
  // mislabeling a missing category as "still personal".
  private async forkCategoriesForOrg(
    transactions: TransactionView[],
    organization: string,
    collections: string[],
  ): Promise<{
    categoryForkMap: Map<string, string>;
    skippedCategoryIds: string[];
    foreignOrgCategoryIds: string[];
  }> {
    const categoryForkMap = new Map<string, string>();
    const skippedCategoryIds: string[] = [];
    const foreignOrgCategoryIds: string[] = [];
    const categoryViews = this.userStore.categories.categoryViews() ?? [];
    const newCategoryViews: CategoryView[] = [];

    for (const categoryId of this.uniqueCategoryIds(transactions)) {
      const category = categoryViews.find((cat) => cat.id === categoryId);
      if (!category) {
        this.logService.warning(
          `forkCategoriesForOrg: no category found for id "${categoryId}"; leaving transaction's category link as-is.`,
        );
        skippedCategoryIds.push(categoryId);
        continue;
      }

      if (category.isOrganizationOwned(organization)) {
        // Already org-owned — don't touch its collections here. This category cipher may be
        // shared with other collections/members for reasons unrelated to this fork, and adding
        // it to `collections` would widen its visibility beyond what was asked for. Under-sharing
        // (a member who only has `collections` access can't yet see it) is the safe failure mode;
        // over-sharing would leak it to whoever has access to `collections` but shouldn't see it.
        continue;
      }

      // `isOrganizationOwned(organization)` false doesn't mean "personal" — a category can also
      // be owned by a *different* org (same ownership-unfiltered picker as addCollectionToExistingFork
      // above). Cloning it here would leak that other org's category into `organization`'s vault,
      // so distinguish "foreign org" from "truly personal" with the no-arg check and never clone
      // the foreign-org case — leaving the link unresolved for other org members (missing share)
      // is the safe failure mode; over-sharing another org's data is not.
      if (category.isOrganizationOwned()) {
        this.logService.warning(
          `forkCategoriesForOrg: category "${category.id}" is owned by a different organization; skipping fork.`,
        );
        foreignOrgCategoryIds.push(categoryId);
        continue;
      }

      // Known gap (same as institution forking above): this doesn't dedupe against an org
      // category already forked from the same source by a prior attempt — e.g. a retry after
      // category forking succeeded but the account save below it failed. Repeat attempts under
      // the same source category will clone a new one each time, orphaning the earlier clone(s).
      // Matching by name alone was tried and reverted — two distinct personal categories (e.g.
      // two different users' own "Groceries") can share a name, and reusing on name match would
      // silently merge them into one org category, misattributing one user's transactions to the
      // other's. There's no stable id linking a clone back to its source (it gets a fresh random
      // id) to dedupe correctly instead, so this is left as a known gap rather than "fixed" with
      // an unsound heuristic.
      //
      // The new id is generated here rather than read back from the save, so it can be used
      // for the fork map below regardless of whether this category ends up saved individually
      // or batched with the others.
      const cat = category.toStoreModel();
      cat.vid = "";
      cat.id = crypto.randomUUID();
      const newView = new CategoryView(cat);
      categoryForkMap.set(categoryId, newView.id);
      newCategoryViews.push(newView);
    }

    if (newCategoryViews.length) {
      // One cipher batch for every still-personal category instead of a sequential save() per
      // category — mirrors the forked-transactions saveToVault call in forkAccountForOrg below.
      await this.userStore.categories.saveToVault(
        newCategoryViews,
        true,
        organization,
        collections,
      );

      // saveToVault always resolves true (see the same caveat on the transactions saveToVault
      // call below), so diff categoryViews() against what we tried to save to recover the
      // failure signal the old per-category save() + null-check used to give this call site.
      const savedIds = new Set(
        (this.userStore.categories.categoryViews() ?? []).map((cat) => cat.id),
      );
      const missing = newCategoryViews.filter((cat) => !savedIds.has(cat.id));
      if (missing.length) {
        throw new Error(
          `Failed to save ${missing.length} of ${newCategoryViews.length} forked categories`,
        );
      }
    }

    return { categoryForkMap, skippedCategoryIds, foreignOrgCategoryIds };
  }

  // Both share paths (fork-and-share, and share-into-existing-fork) land here on success so the
  // user gets a signal the share actually happened — otherwise the selection just clears and a
  // user unsure whether it worked is liable to re-pick and click again, forking a duplicate.
  private showSharedToCollectionToast(teamNames: string[]): void {
    const options: ToastOptions = {
      extraClass: "toast-guide-success",
      toastComponent: GuideSuccessToastComponent,
    };
    this.globalService.showMessage(
      "success",
      this.i18nService.t("sharedWithTeamToastTitle", teamNames.join(", ")),
      this.i18nService.t("sharedWithTeamToastBody"),
      false,
      options,
    );
  }

  private getErrorMessage(e: unknown): string {
    // statusCode 0 = no HTTP response at all (see toNetworkError); its message is raw OS text like
    // "A server with the specified hostname could not be found.". A failed fork save arrives
    // wrapped in PartialVaultWriteError, so check its cause too.
    const cause = e instanceof PartialVaultWriteError ? e.cause : e;
    if (cause instanceof ErrorResponse && cause.statusCode === 0) {
      return this.i18nService.t("networkErrorOccurred");
    }
    if (e instanceof ErrorResponse) {
      return e.getSingleMessage();
    }
    return (e as Error)?.message ?? "Unknown error";
  }

  private async getOrganizationForCollectionOrWarn(
    collectionId: string,
  ): Promise<{ organizationId: string; collectionName: string } | null> {
    const orgCollection = (await this.collectionService.getAllDecrypted()).find(
      (c) => c.id === collectionId,
    );
    if (!orgCollection) {
      this.globalService.showMessage(
        "error",
        "Error",
        "This collection does not belong to any organization; the server rejects an org cipher with none.",
        false,
      );
      return null;
    }
    return { organizationId: orgCollection.organizationId, collectionName: orgCollection.name };
  }

  // Fork account into an org vault, forking its institution and transactions too if not already org-owned.
  private async forkAccountForOrg(
    account: AccountView,
    organization: string,
    collections: string[],
    sourceAccount: AccountView,
  ): Promise<{ skippedCategoryIds: string[]; foreignOrgCategoryIds: string[] }> {
    let institution = this.findInstitutionForAccount(account);

    if (!institution) {
      throw new Error("Invalid/Missing institution");
    }

    if (!institution.isOrganizationOwned(organization)) {
      // Institution belongs to personal/other org vault — clone as new org-owned institution instead of mutating original.
      // Known gap: this doesn't dedupe against an org institution already forked from the same source by a
      // different account share — repeat shares under the same institution will clone a new one each time.
      const inst = institution.toStoreModel();
      inst.vid = "";
      inst.id = crypto.randomUUID();
      // The org copy's accounts never followed the personal institution's link.
      inst.lnkT = null;
      const savedInstitution = await this.userStore.institutions.save(
        new InstitutionView(inst),
        organization,
        collections,
      );
      if (!savedInstitution) {
        throw new Error("Failed to save institution");
      }
      institution = new InstitutionView(savedInstitution as InstitutionStoreModel);
      account.institutionLink = { ...account.institutionLink, institutionId: institution.id };
    }
    // The org copy mustn't follow a personal manual institution's link (institution details moves
    // accounts by rootInstitutionLink).
    account.rootInstitutionLink = null;

    const forkedTransactions = this.transactionsLinkedToAccount(sourceAccount.id).map((tx) => {
      const txModel = tx.toStoreModel();
      txModel.vid = "";
      txModel.id = crypto.randomUUID();
      txModel.acId = account.id;
      return new TransactionView(txModel);
    });

    // Fork categories (and remap the forked transactions' links to them) before the account
    // itself is saved — the account cipher is what commits this fork to the org (a retry re-fetches
    // by the original personal accountId and, seeing no oid set yet, forks again), so if category
    // forking throws it needs to happen while that's still true. Doing it after would leave a
    // half-forked account with no linked transactions in the org on failure, and retrying it would
    // then fork a second, duplicate account on top instead of resuming the same one.
    let skippedCategoryIds: string[] = [];
    let foreignOrgCategoryIds: string[] = [];
    if (forkedTransactions.length) {
      const forkResult = await this.forkCategoriesForOrg(
        forkedTransactions,
        organization,
        collections,
      );
      skippedCategoryIds = forkResult.skippedCategoryIds;
      foreignOrgCategoryIds = forkResult.foreignOrgCategoryIds;
      if (forkResult.categoryForkMap.size) {
        for (const tx of forkedTransactions) {
          for (const allocation of tx.allocations) {
            const newCategoryId = forkResult.categoryForkMap.get(allocation.categoryLink.id);
            if (newCategoryId) {
              allocation.categoryLink = { ...allocation.categoryLink, id: newCategoryId };
            }
          }
        }
      }
    }

    const savedAccount = await this.userStore.accounts.save(account, organization, collections);
    if (!savedAccount) {
      throw new Error("Failed to save account");
    }

    if (!forkedTransactions.length) {
      return { skippedCategoryIds, foreignOrgCategoryIds };
    }

    await this.transactionStoreService.transaction.saveToVault(
      forkedTransactions,
      true,
      organization,
      collections,
    );

    // A dropped create batch rejects saveToVault (commitCreate throws PartialVaultWriteError as
    // of #4957) and propagates from here. This diff covers the narrower case it resolves true on:
    // loadModels() silently skips any saved row that fails validator.assertModel, and saveToVault
    // never checks the returned model count/ids against what was sent. loadModels
    // and triggerChanges both already ran by the time it resolves, so transactionViews() reflects
    // whatever actually made it into the vault; diff that against what we tried to save to recover
    // the failure signal the old per-transaction save() + find() used to give this call site.
    const savedIds = new Set(
      this.transactionStoreService.transaction.transactionViews().map((tx) => tx.id),
    );
    const missing = forkedTransactions.filter((tx) => !savedIds.has(tx.id));
    if (missing.length) {
      throw new Error(
        `Failed to save ${missing.length} of ${forkedTransactions.length} forked transactions`,
      );
    }

    return { skippedCategoryIds, foreignOrgCategoryIds };
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
